import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useGlobal } from '@/context/GlobalContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import {
  LayoutDashboard, Camera, HardDrive, Radio, FileText, Settings, Image,
  Mail, LogOut, Menu, X, Plus, Trash2, Edit, Eye, ChevronRight, FileEdit
} from 'lucide-react';

const AdminLayout = () => {
  const { isAuthenticated, logout, token, username } = useAuth();
  const { API } = useGlobal();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin/login');
    }
  }, [isAuthenticated, navigate]);

  const navItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
    { path: '/admin/page-content', icon: FileEdit, label: 'Page Content' },
    { path: '/admin/camera-modules', icon: Camera, label: 'Camera Modules' },
    { path: '/admin/mdvr', icon: HardDrive, label: 'MDVR & AI' },
    { path: '/admin/ultrasonic', icon: Radio, label: 'Ultrasonic' },
    { path: '/admin/case-studies', icon: FileText, label: 'Case Studies' },
    { path: '/admin/hero-banners', icon: Image, label: 'Hero Banners' },
    { path: '/admin/submissions', icon: Mail, label: 'Contact Submissions' },
    { path: '/admin/settings', icon: Settings, label: 'Global Settings' },
  ];

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
    toast.success('Logged out successfully');
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="p-6 border-b border-slate-800">
          <div className="text-white font-bold text-lg font-['Montserrat']">Trinity CMS</div>
          <div className="text-slate-400 text-sm">Admin Panel</div>
        </div>
        
        <nav className="py-4">
          {navItems.map((item) => (
            <button
              key={item.path}
              onClick={() => {
                navigate(item.path);
                setSidebarOpen(false);
              }}
              className={`admin-nav-item w-full text-left ${isActive(item.path, item.exact) ? 'active' : ''}`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </button>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-800">
          <div className="text-slate-400 text-sm mb-2">Logged in as</div>
          <div className="text-white font-medium mb-4">{username}</div>
          <Button 
            variant="outline" 
            onClick={handleLogout}
            className="w-full border-slate-600 text-slate-300 hover:bg-slate-800"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="admin-content">
        {/* Mobile Header */}
        <div className="lg:hidden flex items-center justify-between mb-6 bg-white p-4 rounded-sm shadow-sm">
          <button onClick={() => setSidebarOpen(true)} className="text-slate-600">
            <Menu className="w-6 h-6" />
          </button>
          <span className="font-bold text-slate-900 font-['Montserrat']">Trinity CMS</span>
          <div className="w-6" />
        </div>

        {/* Mobile Overlay */}
        {sidebarOpen && (
          <div 
            className="lg:hidden fixed inset-0 bg-black/50 z-40"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        <Outlet />
      </div>
    </div>
  );
};

export default AdminLayout;
